import * as React from 'react';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import { answersToMap } from 'Src/helpers/form/formRuntime';
import { FormAnswer, FormDefinition } from 'Src/types/formSchema';

type ProgrammeAnswerField = {
  heading: string;
  type: 'BOOLEAN' | 'ENUM' | 'NUMBER' | 'STRING';
  isRequired?: boolean;
  isDefault?: boolean;
  enumOptions?: string[];
  value?: any;
};

type Props = {
  isOpen: boolean;
  title: string;
  fields: ProgrammeAnswerField[];
  formDefinition?: FormDefinition;
  formAnswers?: FormAnswer[];
  onClose: () => void;
};

export const ClubProgrammeBookingAnswersModal: React.FC<Props> = ({
  isOpen,
  title,
  fields,
  formDefinition,
  formAnswers,
  onClose
}) => {
  if (!isOpen) return null;
  const isV2 = !!formDefinition;

  return (
    <SimpleModal
      isOpen={true}
      title="Booking answers"
      showFooter={false}
      onCloseClick={onClose}
      customClass="pbBookingModal pbBookingAnswersModal"
      dialogStyle={{ maxWidth: 860 }}
    >
      <div className="p-2">
        <div className="cpAnswersHero">
          <div className="cpAnswersHeroTitle">{title}</div>
          <div className="cpAnswersHeroText">Review submitted answers for this booking.</div>
        </div>

        {isV2 ? (
          <FormRendererV2
            definition={formDefinition!}
            answers={answersToMap(formAnswers || [])}
            disabled
            onChange={() => undefined}
          />
        ) : !fields || !fields.length ? (
          <div className="alert alert-light border mb-0">No answers were submitted for this booking.</div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: 12 }}>
            {fields.map((f: ProgrammeAnswerField, idx: number) => {
              const heading = String(f.heading || 'Question');
              const type = String(f.type || 'STRING').toUpperCase();
              const rawValue = typeof f.value === 'undefined' ? undefined : f.value;

              let displayValue: React.ReactNode = '—';

              if (typeof rawValue === 'boolean') {
                displayValue = rawValue ? 'Yes' : 'No';
              } else if (Array.isArray(rawValue)) {
                displayValue = rawValue.length ? rawValue.join(', ') : '—';
              } else if (rawValue !== null && typeof rawValue !== 'undefined' && String(rawValue).trim() !== '') {
                displayValue = String(rawValue);
              }

              return (
                <div key={`${idx}-${heading}`} className="cpAnswerCard">
                  <div className="cpAnswerCardHeader">
                    <div className="cpAnswerHeading">{heading}</div>

                    <div className="cpAnswerMeta">
                      <span className="badge badge-light border">{type}</span>

                      {f.isRequired ? (
                        <span className="badge badge-warning">Required</span>
                      ) : (
                        <span className="badge badge-secondary">Optional</span>
                      )}

                      {f.isDefault ? <span className="badge badge-info">Default field</span> : null}
                    </div>
                  </div>

                  <div className="cpAnswerValue">{displayValue}</div>

                  {type === 'ENUM' && Array.isArray(f.enumOptions) && f.enumOptions.length ? (
                    <div className="cpAnswerHint">Allowed options: {f.enumOptions.join(', ')}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SimpleModal>
  );
};

ClubProgrammeBookingAnswersModal.displayName = 'ClubProgrammeBookingAnswersModal';
